import { Injectable } from "@angular/core";
import { CanActivate, Router } from "@angular/router";
import { IdentityVerificationAction, IdentityVerificationFacade, IdentityVerificationResultStateHelper, SsoAccountCheckoutState, SsoAccountFacade } from "common";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AccountSetupGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    private readonly ssoAccountFacade: SsoAccountFacade,
    private readonly identityVerificationFacade: IdentityVerificationFacade,
  ) { }

  canActivate(): Observable<boolean> {
    return this.ssoAccountFacade.getSsoAccountCheckoutState$()
      .pipe(
        take(1),
        switchMap((checkoutState: SsoAccountCheckoutState) => this.validate(checkoutState))
      )
  }

  private validate(checkoutState: SsoAccountCheckoutState): Observable<boolean> {
    if (checkoutState?.identityVerificationAction === IdentityVerificationAction.Skip) {
      this.router.navigate(["home"]);
      return of(false);
    }

    return combineLatest([this.identityVerificationFacade.isVerificationFailedWithNoRetry(), this.identityVerificationFacade.isVerificationExpiredNoRetry()])
      .pipe(
        take(1),
        switchMap(([isFailed, isExpired]) => this.validateRedirectOnPageWithNoRetry(isFailed, isExpired, checkoutState.errorMessage))
      );
  }

  private validateRedirectOnPageWithNoRetry(isFailed: boolean, isExpired: boolean, errorMessage: string): Observable<boolean> {
    const title = isExpired ? "Verification expired" : undefined;
    if(isFailed || isExpired) {
      IdentityVerificationResultStateHelper.set({ title, message: errorMessage });
      this.router.navigate(['account', 'setup', 'verify-identity-result'], {
          replaceUrl: true,
        });

        return of(false);
      }

    return of(true);
  }
}
